import React from "react";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import { useResponsive } from "../../hooks/useResponsive";
import { BREAKPOINTS } from "../../utils/constants";
import { paginationWindow } from "./paginationWindow";
import {
  PageButton,
  PageEllipsis,
  PaginationBar,
  PaginationControls,
  PaginationSummary,
} from "./styles";
import { PaginationProps } from "./types";

const PaginationComponent: React.FC<PaginationProps> = ({
  page,
  pageCount,
  onPageChange,
  ariaLabel,
  summary,
}) => {
  // Neighbours drop at phone width whatever the consumer's layout: seven 44px targets plus two gaps overflow the card.
  const isPhone = useResponsive(BREAKPOINTS.mobile);
  // A page past the last one stays navigable, so a count that shrank under the reader can be stepped back from.
  const hasControls = pageCount > 1 || page > 0;
  const siblings = isPhone ? 0 : 1;
  const pages = paginationWindow(page, pageCount, siblings);
  const pageItems = pages.map((number, index) => {
    if (number === null) {
      return (
        <PageEllipsis key={`gap-${index}`} aria-hidden={true}>
          …
        </PageEllipsis>
      );
    }
    return (
      <PageButton
        key={number}
        type="button"
        $active={number === page}
        aria-current={number === page ? "page" : undefined}
        aria-label={`Page ${number + 1}`}
        onClick={() => onPageChange(number)}
      >
        {number + 1}
      </PageButton>
    );
  });

  return (
    <PaginationBar aria-label={ariaLabel}>
      <PaginationSummary>{summary}</PaginationSummary>
      {hasControls && (
        <PaginationControls>
          <PageButton
            type="button"
            aria-label="Previous page"
            disabled={page <= 0}
            onClick={() => onPageChange(page - 1)}
          >
            <ChevronLeftRoundedIcon />
          </PageButton>
          {pageItems}
          <PageButton
            type="button"
            aria-label="Next page"
            disabled={page >= pageCount - 1}
            onClick={() => onPageChange(page + 1)}
          >
            <ChevronRightRoundedIcon />
          </PageButton>
        </PaginationControls>
      )}
    </PaginationBar>
  );
};

export const Pagination = React.memo(PaginationComponent);
